import { ApiError, api } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, EllipsisVertical, FileX2, Link2Off } from 'lucide-react';
import { useState } from 'react';

type Panel = 'broken-backlinks' | 'dead-source-links';

/**
 * /admin/wikis/pages 상단 ⋮ 점검 메뉴.
 * - 깨진 백링크 페이지: 본문의 [[slug]] 가 끊긴 페이지 스캔 + 모두 정리(본문에서 펼쳐 제거).
 * - 깨진 원본 링크: 삭제된 출처 문서를 가리키는 페이지 스캔 + 매핑행 정리.
 * LLM 없이 결정적으로 처리.
 */
export function WikiMaintenanceMenu() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [panel, setPanel] = useState<Panel | null>(null);

  return (
    <>
      <div className="relative">
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="inline-flex items-center gap-1 rounded border border-border px-2.5 py-1.5 text-sm hover:border-accent"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <EllipsisVertical className="h-4 w-4" />
          점검
        </button>
        {menuOpen ? (
          <>
            {/* 바깥 클릭 닫기용 백드롭 */}
            <button
              type="button"
              aria-label="메뉴 닫기"
              className="fixed inset-0 z-40 cursor-default"
              onClick={() => setMenuOpen(false)}
            />
            <div
              role="menu"
              className="absolute right-0 z-50 mt-1 w-56 overflow-hidden rounded-md border border-border bg-background shadow-lg"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setPanel('broken-backlinks');
                  setMenuOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-surface"
              >
                <Link2Off className="h-4 w-4 text-foreground-muted" />
                깨진 백링크 페이지
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setPanel('dead-source-links');
                  setMenuOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-surface"
              >
                <FileX2 className="h-4 w-4 text-foreground-muted" />
                깨진 원본 링크
              </button>
            </div>
          </>
        ) : null}
      </div>

      {panel === 'broken-backlinks' ? <BrokenBacklinkPanel onClose={() => setPanel(null)} /> : null}
      {panel === 'dead-source-links' ? (
        <DeadSourceLinkPanel onClose={() => setPanel(null)} />
      ) : null}
    </>
  );
}

function ModalShell({
  title,
  description,
  onClose,
  children,
}: {
  title: string;
  description: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex w-full max-w-2xl max-h-[90vh] flex-col rounded-md border border-border bg-background shadow-xl">
        <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold">{title}</h2>
            <p className="mt-1 text-sm text-foreground-muted">{description}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 text-sm text-foreground-muted hover:text-foreground"
          >
            닫기
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function BrokenBacklinkPanel({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const scanQuery = useQuery({
    queryKey: ['admin', 'wiki', 'broken-backlinks'],
    queryFn: api.adminWikiBrokenBacklinks,
  });

  const cleanupMutation = useMutation({
    mutationFn: api.adminWikiBrokenBacklinksCleanup,
    onSuccess: (r) => {
      setError(null);
      setResult(`페이지 ${r.pagesCleaned}개에서 끊긴 링크 ${r.linksRemoved}개를 정리했습니다.`);
      queryClient.invalidateQueries({ queryKey: ['admin', 'wiki', 'broken-backlinks'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'wiki', 'pages'] });
      queryClient.invalidateQueries({ queryKey: ['wiki'] });
    },
    onError: (err) => {
      setResult(null);
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    },
  });

  const pages = scanQuery.data?.pages ?? [];

  return (
    <ModalShell
      title="깨진 백링크 페이지"
      description="본문의 [[링크]]가 삭제·부재한 페이지를 가리키는 경우입니다. 정리하면 해당 링크를 본문에서 일반 텍스트로 펼쳐 제거합니다."
      onClose={onClose}
    >
      <div className="flex-1 overflow-auto px-5 py-4">
        {scanQuery.isLoading ? (
          <p className="text-sm text-foreground-muted">스캔 중…</p>
        ) : pages.length === 0 ? (
          <p className="text-sm text-success">깨진 백링크가 없습니다.</p>
        ) : (
          <ul className="space-y-2">
            {pages.map((p) => (
              <li key={p.slug} className="rounded border border-border-muted px-3 py-2">
                <div className="text-sm font-medium">{p.title}</div>
                <div className="font-mono text-xs text-foreground-faint">{p.slug}</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {p.deadSlugs.map((s) => (
                    <code
                      key={s}
                      className="rounded bg-destructive/10 px-1 text-xs text-destructive"
                    >
                      [[{s}]]
                    </code>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Footer
        affected={pages.length}
        result={result}
        error={error}
        pending={cleanupMutation.isPending}
        onCleanup={() => cleanupMutation.mutate()}
      />
    </ModalShell>
  );
}

function DeadSourceLinkPanel({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const scanQuery = useQuery({
    queryKey: ['admin', 'wiki', 'dead-source-links'],
    queryFn: api.adminWikiDeadSourceLinks,
  });

  const cleanupMutation = useMutation({
    mutationFn: api.adminWikiDeadSourceLinksCleanup,
    onSuccess: (r) => {
      setError(null);
      const skip =
        r.skipped.length > 0
          ? ` (유일 출처라 건너뜀: ${r.skipped.length}개 — ${r.skipped.join(', ')})`
          : '';
      setResult(
        `페이지 ${r.pagesAffected}개에서 매핑 ${r.mappingsRemoved}개를 정리했습니다.${skip}`,
      );
      queryClient.invalidateQueries({ queryKey: ['admin', 'wiki', 'dead-source-links'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'wiki', 'pages'] });
      queryClient.invalidateQueries({ queryKey: ['wiki'] });
    },
    onError: (err) => {
      setResult(null);
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    },
  });

  const pages = scanQuery.data?.pages ?? [];

  return (
    <ModalShell
      title="깨진 원본 링크"
      description="삭제된 출처 문서를 가리키는 페이지입니다. 정리하면 그 매핑을 제거합니다. 그 문서가 유일 출처라 고아가 되는 페이지는 건너뜁니다."
      onClose={onClose}
    >
      <div className="flex-1 overflow-auto px-5 py-4">
        {scanQuery.isLoading ? (
          <p className="text-sm text-foreground-muted">스캔 중…</p>
        ) : pages.length === 0 ? (
          <p className="text-sm text-success">깨진 원본 링크가 없습니다.</p>
        ) : (
          <ul className="space-y-2">
            {pages.map((p) => (
              <li key={p.slug} className="rounded border border-border-muted px-3 py-2">
                <div className="text-sm font-medium">{p.title}</div>
                <div className="font-mono text-xs text-foreground-faint">{p.slug}</div>
                <div className="mt-1 flex flex-wrap items-center gap-1">
                  <AlertTriangle className="h-3 w-3 text-destructive" />
                  {p.deadDocNames.map((n, i) => (
                    <span key={`${n}-${i}`} className="text-xs text-destructive">
                      {n}
                    </span>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Footer
        affected={pages.length}
        result={result}
        error={error}
        pending={cleanupMutation.isPending}
        onCleanup={() => cleanupMutation.mutate()}
      />
    </ModalShell>
  );
}

function Footer({
  affected,
  result,
  error,
  pending,
  onCleanup,
}: {
  affected: number;
  result: string | null;
  error: string | null;
  pending: boolean;
  onCleanup: () => void;
}) {
  return (
    <div className="border-t border-border px-5 py-4">
      {result ? <p className="mb-2 text-sm text-success">{result}</p> : null}
      {error ? <p className="mb-2 text-sm text-destructive">{error}</p> : null}
      <button
        type="button"
        onClick={onCleanup}
        disabled={pending || affected === 0}
        className="rounded bg-accent px-4 py-2 text-sm font-medium text-accent-foreground disabled:opacity-50"
      >
        {pending ? '정리 중…' : '모두 정리'}
      </button>
    </div>
  );
}
